import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Spinner } from "../components/ui/Spinner";
import { useAuth } from "../lib/auth";
import * as api from "../lib/api";
import * as b2c from "../lib/b2c";

/** Landing point for the B2C redirect: exchanges ?code= for tokens. */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const back = await b2c.completeLogin();
        // Prove the token is accepted by the API before navigating, so a
        // rejected token shows a real reason instead of bouncing to /login.
        await api.fetchMe();
        await refresh();
        if (!cancelled) navigate(back, { replace: true });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "sign-in failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, refresh]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 ring-1 ring-inset ring-rose-100">
          <AlertTriangle className="h-7 w-7" />
        </span>
        <h1 className="mt-5 text-xl font-bold text-slate-900">Sign-in didn't complete</h1>
        <p className="mt-2 max-w-md text-sm text-slate-500">{error}</p>
        <Link to="/login" className="mt-6 font-medium text-brand-700 hover:underline">
          Try again
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <Spinner className="h-8 w-8 text-brand-500" />
      <p className="text-sm text-slate-500">Signing you in…</p>
    </div>
  );
}
